/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import { useHelpSimulationActive } from "context/HelpSimulationContext";
import { scrollToHelpTop } from "help/scrollToHelpTop";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const helpSimulationActive = useHelpSimulationActive();
  const [userName, setUserName] = useState("");
  const [isOffline, setIsOffline] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    // Get user data from localStorage
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData && userData.name) {
        setUserName(userData.name);
      }
    } catch (err) {
      console.error("Error getting user data:", err);
    }

    // Check online status
    const handleOnlineStatus = () => {
      setIsOffline(!navigator.onLine);
    };

    // Set initial online status
    handleOnlineStatus();

    // Add event listeners for online/offline changes
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener("scroll", handleTransparentNavbar);
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => {
    if (helpSimulationActive) {
      scrollToHelpTop();
      return;
    }
    setMiniSidenav(dispatch, !miniSidenav);
  };

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 2, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <MDBox display="flex" alignItems="center">
            <Tooltip
              title={helpSimulationActive ? "Go to help menu" : "Toggle navigation"}
              placement="bottom"
            >
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={(theme) => navbarMobileMenu(theme)}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {helpSimulationActive ? "keyboard_arrow_up" : miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
            </Tooltip>
            <Breadcrumbs 
              icon="home" 
              title={route[route.length - 1]}
              route={route} 
              light={light} 
            />
            {isOffline && (
              <MDTypography
                variant="h6"
                fontWeight="medium"
                color="error"
                ml={1}
              >
                (Working Offline)
              </MDTypography>
            )}
          </MDBox>
          {userName && (
            <MDBox
              ml={2}
              display={{ xs: "none", md: "flex" }}
              alignItems="center"
              color={light ? "white" : "inherit"}
            >
              <Icon sx={iconsStyle}>person</Icon>
              <MDBox ml={1}>
                <MDTypography variant="button" fontWeight="medium" color={light ? "white" : "dark"}>
                  Welcome, {userName}
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox />
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
